/** Calendar schedule status keys used by user_calendar_daily markers. */
export const CALENDAR_STATUS_KEYS = [
  'pending',
  'in_progress',
  'service_done',
  'completed',
  'cancelled',
] as const;

export type CalendarStatusKey = (typeof CALENDAR_STATUS_KEYS)[number];

export type CalendarDayStatusCounts = Record<CalendarStatusKey, number>;

export const EMPTY_CALENDAR_STATUS_COUNTS: CalendarDayStatusCounts = {
  pending: 0,
  in_progress: 0,
  service_done: 0,
  completed: 0,
  cancelled: 0,
};

export const CALENDAR_STATUS_META: Record<
  CalendarStatusKey,
  { label: string; dotClass: string; badgeClass: string }
> = {
  pending: {
    label: 'Pending',
    dotClass: 'bg-orange-500',
    badgeClass: 'bg-orange-50 text-orange-600 border-orange-200',
  },
  in_progress: {
    label: 'In Progress',
    dotClass: 'bg-blue-300',
    badgeClass: 'bg-blue-50 text-blue-500 border-blue-200',
  },
  service_done: {
    label: 'Service Done',
    dotClass: 'bg-sky-400',
    badgeClass: 'bg-sky-50 text-sky-600 border-sky-200',
  },
  completed: {
    label: 'Completed',
    dotClass: 'bg-lime-500',
    badgeClass: 'bg-lime-50 text-lime-700 border-lime-200',
  },
  cancelled: {
    label: 'Cancelled',
    dotClass: 'bg-gray-400',
    badgeClass: 'bg-gray-50 text-gray-500 border-gray-200',
  },
};

export function createEmptyCalendarStatusCounts(): CalendarDayStatusCounts {
  return { ...EMPTY_CALENDAR_STATUS_COUNTS };
}

export function hasCalendarStatusCounts(counts: CalendarDayStatusCounts | null | undefined): boolean {
  if (!counts) return false;
  return CALENDAR_STATUS_KEYS.some((key) => (counts[key] ?? 0) > 0);
}

export function activeCalendarStatuses(
  counts: CalendarDayStatusCounts,
): Array<{ key: CalendarStatusKey; count: number }> {
  return CALENDAR_STATUS_KEYS
    .map((key) => ({ key, count: counts[key] ?? 0 }))
    .filter((item) => item.count > 0);
}

/** 与后端 TeamOrder.status 语义一致：100/0 pending，1 in_progress，2 service_done，3/6 completed，4 cancelled */
export type OrderUiStatus = CalendarStatusKey;

export const ORDER_UI_STATUS_KEYS: OrderUiStatus[] = [...CALENDAR_STATUS_KEYS];

export const ORDER_STATUS_META: Record<
  OrderUiStatus,
  { label: string; dotClass: string; badgeClass: string }
> = CALENDAR_STATUS_META;

export function getOrderStatusMeta(status: string): {
  label: string;
  dotClass: string;
  badgeClass: string;
} {
  if (status in ORDER_STATUS_META) {
    return ORDER_STATUS_META[status as OrderUiStatus];
  }
  return ORDER_STATUS_META.pending;
}
